import { Controller } from '@hotwired/stimulus'
import { post } from '@rails/request.js'

export default class extends Controller {
  static get values() {
    return {
      appStateUrl: String,
      sessionUrl: String,
      bootstrapSession: Boolean,
      primaryColor: String,
      colorScheme: String,
      backgroundColor: String,
      fcm: String,
      mobilePlatform: String,
      mobilePlatformVersion: String,
      mobileAppVersion: String,
    }
  }

  connect() {
    // seeded from the session, so an unchanged app state costs no request
    this.knownState = {
      firebase_fcm: this.fcmValue || null,
      mobile_platform: this.mobilePlatformValue || null,
      mobile_platform_version: this.mobilePlatformVersionValue || null,
      mobile_app_version: this.mobileAppVersionValue || null,
    }
    this.sessionBootstrapped = false
    this.listener = (message) => this.receiveEvent(message)

    window.addEventListener('message', this.listener) // for ios
    document.addEventListener('message', this.listener) // for android

    this.sendEvent('ping')
    this.sendEvent('SystemState', {
      primaryColor: this.primaryColorValue,
      backgroundColor: this.backgroundColorValue,
      theme: this.colorSchemeValue,
    })
  }

  disconnect() {
    window.removeEventListener('message', this.listener) // for ios
    document.removeEventListener('message', this.listener) // for android
  }

  sendEvent(eventName, eventData) {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ eventName, eventData })
    )
  }

  receiveEvent({ data }) {
    if (typeof data !== 'string') return

    let message
    try {
      message = JSON.parse(data)
    } catch {
      return
    }

    if (!message || typeof message !== 'object' || !('eventName' in message))
      return

    switch (message.eventName) {
      case 'AppState':
        this.onAppStateChange(message.eventData)
        if (message.eventData?.safeArea) {
          this.setSafeArea(message.eventData.safeArea)
        }
        break
    }
  }

  async onAppStateChange(eventData) {
    if (!eventData) return

    this.bootstrapSession(eventData.firebaseToken)

    const changes = this.changedState({
      firebase_fcm: eventData.firebaseFcm,
      mobile_platform: eventData.mobilePlatform,
      mobile_platform_version: eventData.mobilePlatformVersion,
      mobile_app_version: eventData.appVersion,
    })

    if (Object.keys(changes).length === 0) return

    try {
      const response = await post(this.appStateUrlValue, { body: changes })
      if (response.ok) {
        Object.assign(this.knownState, changes)
      } else {
        console.error('Failed to sync mobile app state', response)
      }
    } catch (error) {
      console.error('Error syncing mobile app state', error)
    }
  }

  // outside knownState: the token is reissued every hour and would diff on every ping
  async bootstrapSession(firebaseToken) {
    if (
      !this.bootstrapSessionValue ||
      this.sessionBootstrapped ||
      !firebaseToken
    )
      return

    this.sessionBootstrapped = true

    try {
      const response = await post(this.sessionUrlValue, {
        body: { firebase_token: firebaseToken },
      })

      // 204 means nothing was created; only 201 changes what the page should render
      if (response.statusCode === 201) window.location.reload()
    } catch (error) {
      console.error('Error bootstrapping mobile app session', error)
    }
  }

  changedState(state) {
    const changes = {}

    Object.entries(state).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return

      // the platform sends versions as numbers, the session holds strings
      const normalized = String(value)
      if (normalized !== this.knownState[key]) changes[key] = normalized
    })

    return changes
  }

  setSafeArea({ top, bottom }) {
    const bodyStyle = document.documentElement.style

    bodyStyle.setProperty('--safe-area-top', `${top}px`)
    bodyStyle.setProperty('--safe-area-bottom', `${bottom}px`)
  }
}
